import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    attachAvailabilityDispatch,
    resetAvailability,
    enqueueAutoAvailability,
    getAvailStatus,
    subscribeAvailKey,
    getAvailProgress,
    subscribeAvailProgress,
} from '../services/availabilityQueue';
import { isTrainSegment, segKeyOf } from '../utils/seatAvailability';

const TOP_BAND = 12; // matches the first page of TravelResultsList

const routeSig = (r) => (r?.nodes || []).map((n) => n?.segment?.train_num || n?.segment?.flight_num || n?.segment?.service_name || '').join('-');

// Drives the auto availability queue from the streamed results. The displayed
// (sorted, filtered) list sets priority, so the cards the user sees load first.
export const useAutoAvailability = ({ allRoutes, displayedRoutes, enabled }) => {
    const dispatch = useDispatch();

    useEffect(() => { attachAvailabilityDispatch(dispatch); }, [dispatch]);
    useEffect(() => () => resetAvailability(), []);

    const displayedRef = useRef(displayedRoutes);
    displayedRef.current = displayedRoutes;
    const orderSig = (displayedRoutes || []).slice(0, TOP_BAND).map(routeSig).join('|');

    useEffect(() => {
        if (!enabled || !allRoutes || allRoutes.length === 0) return;

        const items = [];
        let rank = 0;
        const visit = (route) => {
            const r = rank++;
            (route?.nodes || []).forEach((n, legIdx) => {
                const seg = n?.segment;
                if (!isTrainSegment(seg) || !segKeyOf(seg)) return;
                // One job per train segment; the queue walks its classes in order
                const tier = r < TOP_BAND ? 1 : 2;
                items.push({ seg, prio: tier * 1e7 + r * 10 + legIdx });
            });
        };

        (displayedRef.current || []).forEach(visit);
        allRoutes.forEach(visit);
        enqueueAutoAvailability(items);
    }, [allRoutes, orderSig, enabled]);
};

// Status of several availability cells: { [reqKey]: 'idle' | 'queued' | 'loading' | 'done' | 'error' }
export const useAvailStatuses = (keys) => {
    const list = (keys || []).filter(Boolean);
    const keyStr = list.join('\n');
    const read = () => (keyStr ? keyStr.split('\n').map(getAvailStatus).join('\n') : '');
    const [snap, setSnap] = useState(read);

    useEffect(() => {
        const ks = keyStr ? keyStr.split('\n') : [];
        const update = () => setSnap(ks.map(getAvailStatus).join('\n'));
        update();
        const unsubs = ks.map((k) => subscribeAvailKey(k, update));
        return () => unsubs.forEach((u) => u());
    }, [keyStr]);

    return useMemo(() => {
        const ks = keyStr ? keyStr.split('\n') : [];
        const ss = snap ? snap.split('\n') : [];
        const out = {};
        ks.forEach((k, i) => { out[k] = ss.length === ks.length ? ss[i] : getAvailStatus(k); });
        return out;
    }, [keyStr, snap]);
};

// { primaryTotal, primaryDone, primaryPending, totalPending } for the auto queue
export const useAvailabilityProgress = () => {
    const [p, setP] = useState(getAvailProgress);
    useEffect(() => {
        setP(getAvailProgress());
        return subscribeAvailProgress(() => setP(getAvailProgress()));
    }, []);
    return p;
};
